import { Table } from '@tanstack/react-table';

import { Thead, TheadProps, Tr } from '@oaknorth/component-core-v2';

import { flexRender } from '../../flexRender';
import { type TableHeaderProps as HeaderRenderProps } from '../../types';
import { allParentColumnsVisible } from '../all-parent-columns-visible';
import { Header } from '../Header';

interface TableHeaderProps<T extends object = {}> extends TheadProps {
  table: Table<T>;

  /**
   * Sticks the table header to the top of the page when scrolling vertically
   */
  isSticky?: boolean;

  borderBetweenParentRows?: boolean;
}

export const TableHeader = <T extends object = {}>({
  table,
  isSticky,
  borderBetweenParentRows = true,
  ...props
}: TableHeaderProps<T>) => (
  <Thead {...props}>
    {table.getHeaderGroups().map((headerGroup, index) => (
      <Tr
        key={headerGroup.id}
        {...(isSticky ? { position: 'sticky', top: 0, zIndex: 'banner', background: 'white' } : {})}
      >
        {headerGroup.headers.map((header) => {
          const { tableHeader } = header.column.columnDef.meta || {};

          const areAllParentColumnsVisible = allParentColumnsVisible(header.column, table);
          if (!areAllParentColumnsVisible) {
            return null;
          }

          if (typeof tableHeader !== 'undefined') {
            return flexRender(tableHeader, { key: header.id, header } satisfies HeaderRenderProps<T>);
          }

          const showBottomBorder = borderBetweenParentRows || index === table.getHeaderGroups().length - 1;

          return (
            <Header
              key={header.id}
              header={header}
              borderBottom={showBottomBorder ? '1px solid' : 'none'}
              borderColor="border"
            />
          );
        })}
      </Tr>
    ))}
  </Thead>
);
